import { Box } from '@src/atoms/Box';
import React, { FC, memo, useCallback, useMemo, useRef } from 'react';
import { LegoV2Context } from './context';
import { LegoControllerRxjs } from './controller';
import { ILegoContext, ILegoProps } from './models';
import { AnyType } from '@src/models/common';
import { getCommonElementYupValidator, getElementYupValidator } from './utils';

const LegoBase: FC<Pick<ILegoProps, 'elements'>> = ({ elements }) => {
  return (
    <Box column gap={4} fullWidth>
      {elements.map((props) => (
        <props.Component key={props.id} {...props} />
      ))}
    </Box>
  );
};

/**
 * LegoV2 - это упрощенная и оптимизированная версия Lego
 *
 * - Полная инструкция @link https://centrateam.atlassian.net/wiki/spaces/CEN/pages/443351043/Lego+V2
 * - Макеты для диалогов @link https://www.figma.com/design/7tSylW4PyQ3ZacyQYja5uK/Web-System?node-id=7833-3096&node-type=instance&m=dev
 *
 *
 * Краткая инструкция:
 * Версия V2 дает вам больше контроля над элементами и больше оптимизации при создании формы
 *
 * 1. Создать константы с `id` ваших элементов в файле `const.ts`
 * 2. Создайте конфиг из лего элементов. **ОЧЕНЬ ВАЖНО: постарайтесь сделать конфиг статической константой, а не результатом выполнения функции.**
 * 2.1. Если вам нужно какая-то особенная логика, то добавьте в папке `elements` файл с вашим компонентом,
 * где можно описать всю нужную логику
 * 3. Создайте экземпляр контроллера (чаще не нужно) `new LegoControllerRxjs()` если вам нужно больше управления
 * это делается для того, чтобы вы могли управлять формой где-то далеко от текущего расположения
 *
 * Хуки лего:
 * - `useLegoV2Context` - хук для получения экземпляра контроллера или основных фунций onSubmit, validate
 * - `useLegoElementValue` - основной хук, который позваляет получить `{ value, onChange }` для управления любым лего элементом.
 * Этот хук позваляет подписываться на любые внутренние значения формы и сторить логику на их основе.
 * - `useLegoElementError` - хук для получения ошибки валиадции элемента
 * - `useExternalDependenceValues` - хук для подписки на внещние зависимости. Посмотри примеры работы в `@centra/centra-ui`
 *
 * Для более гибкого управления состоянием используйте хуки из объекта `LegoV2Hooks`
 *
 * Вот еще некоторые кейсы, которые помогут вам в разработке
 * 1. Если вам нужно чтобы один из элементов реагировал на какое-либо состояние из вне,
 * используйте метод `onChangeExternalDependence` у контроллера, для изменения этого состояния. Я для самого лего
 * элемента можно оформить подписку с помощью хука `useExternalDependenceValues`
 *
 * 2. Вы можете подписаться на значение любого элемента в форме используя хук `useLegoElementValue`
 * тем самым гибко отрисовывать нужные вам значения
 */
export const LegoV2: FC<ILegoProps> = memo((props) => {
  const controller = useRef(props.controller || new LegoControllerRxjs());

  const validate = useCallback(
    (legoId: string, v: AnyType) => {
      const validators = props.elements.find((e) => e.id === legoId)?.validators;
      if (!validators) return;

      const schema = getElementYupValidator(validators);
      schema
        ?.validate(v)
        .then(() => controller.current.onChangeError(legoId))
        .catch((e) => controller.current.onChangeError(legoId, e.errors));
    },
    [props.elements],
  );

  const onSubmit = useCallback(() => {
    const schema = getCommonElementYupValidator(props.elements);

    schema
      ?.validate(controller.current.getValueList(), { abortEarly: false, recursive: true })
      .then((v) => props.onSubmit(v))
      .catch((err) => {
        const errorFields = err.inner.reduce((acc, error) => {
          // TODO: вот здесь возможно стоит сделать через push добавление, если будет несколько сообщений
          // Но пока работает так
          acc[error.path] = [error.message]; // Привязываем сообщение к полю
          return acc;
        }, {});

        controller.current.onChangeCommonError(errorFields);
      });
  }, []);

  const value = useMemo<ILegoContext>(
    () => ({
      controller: controller.current,
      onSubmit,
      validationMode: props.validationMode || 'onChange',
      validate,
    }),
    [onSubmit, props.validationMode, validate],
  );

  return (
    <LegoV2Context.Provider value={value}>
      <LegoBase elements={props.elements} />
    </LegoV2Context.Provider>
  );
});
